import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Info,
  Lightbulb,
  Link2,
  Star,
  type LucideIcon,
} from 'lucide-react';

/**
 * Las piezas con las que se escribe una pagina de ayuda.
 *
 * Van juntas porque son pequenas y se usan siempre en compania unas de otras:
 * un archivo por cada una serian ocho importaciones en cada pagina para pintar
 * una tabla y un aviso.
 */

/* ------------------------------------------------------------ apartado --- */

/**
 * Un apartado, con su enlace permanente.
 *
 * El `id` va en el propio encabezado y se marca con `data-dk-seccion`, que es
 * lo que lee la tabla de contenidos. No hay una lista de apartados declarada
 * en ningun otro sitio: la tabla se construye de lo que la pagina ha pintado
 * de verdad, asi que no puede quedarse desfasada ni ofrecer un salto a un
 * apartado que ya no existe.
 */
export function Apartado({
  id,
  titulo,
  children,
  nivel = 2,
}: {
  id: string;
  titulo: string;
  children: ReactNode;
  nivel?: 2 | 3;
}): JSX.Element {
  const [copiado, setCopiado] = useState(false);
  const Titulo = nivel === 3 ? 'h3' : 'h2';

  const copiarEnlace = async (): Promise<void> => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1600);
    } catch {
      /* Sin permiso de portapapeles, el enlace sigue en la barra del navegador. */
    }
  };

  return (
    <section className="dk-anclable mt-10 first:mt-0">
      <Titulo
        id={id}
        data-dk-seccion={id}
        data-dk-nivel={nivel}
        className={`dk-titulo-seccion group flex scroll-mt-24 items-center gap-2 font-semibold tracking-tight ${
          nivel === 3 ? 'text-[17px]' : 'text-[22px]'
        }`}
        style={{ color: 'var(--dk-texto)' }}
      >
        {titulo}
        <button
          type="button"
          onClick={() => void copiarEnlace()}
          aria-label={`Copiar enlace a «${titulo}»`}
          title="Copiar enlace a este apartado"
          className="dk-ancla rounded p-1"
          style={{ color: copiado ? 'var(--dk-tip-marca)' : 'var(--dk-texto-tenue)' }}
        >
          {copiado ? <Check size={15} /> : <Link2 size={15} />}
        </button>
      </Titulo>

      <div className="dk-prosa mt-3">{children}</div>
    </section>
  );
}

/* --------------------------------------------------------------- aviso --- */

/**
 * Aviso destacado dentro del texto.
 *
 * Cuatro tipos y ni uno mas. Con ocho colores distintos nadie recuerda cual
 * era el grave, y el que de verdad avisa de perder algo deja de destacar.
 *
 *   consejo   atajo o forma mas comoda de hacer algo
 *   info      contexto util que no cambia lo que hay que hacer
 *   aviso     puede salir mal o perderse algo
 *   clave     hay que saberlo si o si para entender el resto
 */
const TIPOS: Record<string, { icono: LucideIcon; rotulo: string; fondo: string }> = {
  consejo: { icono: Lightbulb, rotulo: 'Consejo', fondo: 'tip' },
  info: { icono: Info, rotulo: 'Informacion', fondo: 'info' },
  aviso: { icono: AlertTriangle, rotulo: 'Atencion', fondo: 'aviso' },
  clave: { icono: Star, rotulo: 'Importante', fondo: 'clave' },
};

export function Aviso({
  tipo = 'info',
  titulo,
  children,
}: {
  tipo?: 'consejo' | 'info' | 'aviso' | 'clave';
  titulo?: string;
  children: ReactNode;
}): JSX.Element {
  const { icono: Icono, rotulo, fondo } = TIPOS[tipo] ?? TIPOS.info;

  return (
    <div
      role="note"
      className="my-5 flex gap-3 rounded-xl border-l-4 px-4 py-3"
      style={{
        background: `var(--dk-${fondo}-fondo)`,
        borderColor: `var(--dk-${fondo}-marca)`,
      }}
    >
      <Icono size={17} className="mt-0.5 shrink-0" style={{ color: `var(--dk-${fondo}-marca)` }} />
      <div className="min-w-0 flex-1">
        {/* El titulo usa el token de TEXTO, no el de marca: el de marca esta
            elegido para destacar, no para leerse encima de su propio fondo. */}
        <p className="mb-1 text-[13px] font-semibold" style={{ color: `var(--dk-${fondo}-texto)` }}>
          {titulo ?? rotulo}
        </p>
        <div className="dk-prosa text-[14px] [&>p:last-child]:mb-0">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ reticula --- */

export function Reticula({
  columnas = 2,
  children,
}: {
  columnas?: 2 | 3;
  children: ReactNode;
}): JSX.Element {
  const clases = columnas === 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2';
  return <div className={`my-5 grid gap-3 ${clases}`}>{children}</div>;
}

/**
 * Tarjeta. Si lleva `a`, es un enlace y se comporta como tal: recuadro de
 * foco, cursor y navegacion con teclado los pone el propio `<Link>`.
 */
export function Tarjeta({
  icono: Icono,
  titulo,
  children,
  a,
}: {
  icono?: LucideIcon;
  titulo: string;
  children?: ReactNode;
  a?: string;
}): JSX.Element {
  const contenido = (
    <>
      {Icono ? (
        <span
          className="mb-2.5 flex h-9 w-9 items-center justify-center rounded-lg"
          style={{ background: 'var(--dk-acento-suave)', color: 'var(--dk-acento-texto)' }}
        >
          <Icono size={18} />
        </span>
      ) : null}
      <p className="text-[14px] font-semibold" style={{ color: 'var(--dk-texto)' }}>
        {titulo}
      </p>
      {children ? (
        <p className="mt-1 text-[13px] leading-relaxed" style={{ color: 'var(--dk-texto-tenue)' }}>
          {children}
        </p>
      ) : null}
      {a ? (
        <span
          className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium"
          style={{ color: 'var(--dk-acento-texto)' }}
        >
          Abrir <ArrowRight size={13} />
        </span>
      ) : null}
    </>
  );

  const estilo = { background: 'var(--dk-fondo-elevado)', borderColor: 'var(--dk-borde)' };

  if (a) {
    return (
      <Link
        to={a}
        className="block rounded-xl border p-4 transition-shadow hover:shadow-md"
        style={estilo}
      >
        {contenido}
      </Link>
    );
  }

  return (
    <div className="rounded-xl border p-4" style={estilo}>
      {contenido}
    </div>
  );
}

/* --------------------------------------------------------------- tabla --- */

/**
 * Tabla con desbordamiento propio.
 *
 * El contenedor se desplaza en horizontal por su cuenta; si no, una tabla de
 * cinco columnas ensancha la pagina entera y el texto de alrededor deja de
 * caber en una pantalla estrecha.
 */
export function Tabla({
  cabeceras,
  filas,
}: {
  cabeceras: ReactNode[];
  filas: ReactNode[][];
}): JSX.Element {
  return (
    <div
      className="my-5 overflow-x-auto rounded-xl border"
      style={{ borderColor: 'var(--dk-borde)' }}
    >
      <table className="w-full border-collapse text-[13.5px]">
        <thead>
          <tr style={{ background: 'var(--dk-fondo-sutil)' }}>
            {cabeceras.map((cabecera, i) => (
              <th
                key={i}
                scope="col"
                className="whitespace-nowrap px-3.5 py-2.5 text-left font-semibold"
                style={{ color: 'var(--dk-texto)' }}
              >
                {cabecera}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((fila, i) => (
            <tr key={i} className="border-t" style={{ borderColor: 'var(--dk-borde)' }}>
              {fila.map((celda, j) => (
                <td
                  key={j}
                  className="px-3.5 py-2.5 align-top"
                  style={{ color: j === 0 ? 'var(--dk-texto)' : 'var(--dk-texto-suave)' }}
                >
                  {celda}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* --------------------------------------------------------------- pasos --- */

/** Lista numerada de pasos, para los tutoriales. */
export function Pasos({ children }: { children: ReactNode }): JSX.Element {
  return <ol className="my-5 space-y-3">{children}</ol>;
}

export function Paso({
  numero,
  titulo,
  children,
}: {
  numero: number;
  titulo: string;
  children?: ReactNode;
}): JSX.Element {
  return (
    <li className="flex gap-3">
      <span
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-bold"
        style={{ background: 'var(--dk-acento-boton)', color: '#fff' }}
      >
        {numero}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[14px] font-semibold" style={{ color: 'var(--dk-texto)' }}>
          {titulo}
        </p>
        {children ? (
          <div className="dk-prosa mt-0.5 text-[14px] [&>p:last-child]:mb-0">{children}</div>
        ) : null}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------- ejemplo --- */

/**
 * Ejemplo de «asi si / asi no».
 *
 * Los dos lados a la vez y no solo el bueno: la diferencia entre un ticket
 * util y uno inutil no se explica, se ve. Quien lee el malo se reconoce en el,
 * y esa es toda la leccion.
 */
export function Ejemplo({
  bien,
  mal,
}: {
  bien: { titulo?: string; children: ReactNode };
  mal: { titulo?: string; children: ReactNode };
}): JSX.Element {
  return (
    <div className="my-5 grid gap-3 sm:grid-cols-2">
      <div
        className="rounded-xl border-l-4 px-4 py-3"
        style={{ background: 'var(--dk-tip-fondo)', borderColor: 'var(--dk-tip-marca)' }}
      >
        <p className="mb-1.5 text-[12px] font-bold uppercase tracking-wide" style={{ color: 'var(--dk-tip-texto)' }}>
          {bien.titulo ?? 'Asi si'}
        </p>
        <div className="dk-prosa text-[13.5px] [&>p:last-child]:mb-0">{bien.children}</div>
      </div>
      <div
        className="rounded-xl border-l-4 px-4 py-3"
        style={{ background: 'var(--dk-clave-fondo)', borderColor: 'var(--dk-clave-marca)' }}
      >
        <p className="mb-1.5 text-[12px] font-bold uppercase tracking-wide" style={{ color: 'var(--dk-clave-texto)' }}>
          {mal.titulo ?? 'Asi no'}
        </p>
        <div className="dk-prosa text-[13.5px] [&>p:last-child]:mb-0">{mal.children}</div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ etiqueta --- */

/** Una etiqueta de estado, para poder ensenar en el texto lo que se ve en pantalla. */
export function Etiqueta({ children, tono = 'neutro' }: { children: ReactNode; tono?: 'neutro' | 'acento' }): JSX.Element {
  return (
    <span
      className="inline-flex items-center rounded-md px-1.5 py-0.5 text-[12px] font-semibold"
      style={
        tono === 'acento'
          ? { background: 'var(--dk-acento-suave)', color: 'var(--dk-acento-texto)' }
          : { background: 'var(--dk-codigo-fondo)', color: 'var(--dk-texto)' }
      }
    >
      {children}
    </span>
  );
}
